import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Cart } from "../../types/PricingCalculator";
import { formatCurrency, convert } from "../../utils/pricingLogic";
import { generatePDF } from "../../utils/pdfGenerator";
import {
  generateWhatsAppShareLink,
  encodeCartToUrl,
} from "../../utils/urlEncoder";

interface SummaryStepProps {
  cart: Cart;
  onEditStep: (step: string) => void;
}

const SummaryStep: React.FC<SummaryStepProps> = ({ cart, onEditStep }) => {
  const [copied, setCopied] = useState(false);
  const { i18n, t } = useTranslation();
  const lang = i18n.language as string;
  const pickDescription = (base?: {
    description?: string;
    description_fr?: string;
    description_rw?: string;
  }) => {
    if (!base) return "";
    if (lang.startsWith("fr") && base.description_fr)
      return base.description_fr;
    if ((lang === "rw" || lang.startsWith("rw")) && base.description_rw)
      return base.description_rw;
    return base.description || "";
  };
  const hardwareItems = [
    cart.selections.screen,
    cart.selections.printer,
    cart.selections.scanner,
    ...(cart.selections.adapters || []),
  ].filter(Boolean);

  const handleEditStep = (step: string) => {
    onEditStep(step);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-300">
          {t("pricingCalc.summary.heading")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">{t("pricingCalc.summary.subheading")}</p>
      </div>

      {/* Configuration Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("pricingCalc.summary.config")}
        </h3>

        <div className="space-y-4">
          {/* Eligibility */}
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <div>
              <span className="text-gray-700">
                {t("pricingCalc.summary.eligibility")}
              </span>
              <span
                className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  cart.eligibility.isFree
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {cart.eligibility.isFree
                  ? t("pricingCalc.summary.free")
                  : t("pricingCalc.summary.paidPlan")}
              </span>
            </div>
            <button
              onClick={() => handleEditStep("eligibility")}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Edit
            </button>
          </div>

          {/* Device Strategy */}
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <div>
              <span className="text-gray-700">
                {t("pricingCalc.summary.deviceStrategy")}
              </span>
              <span className="ml-2 font-medium dark:text-gray-800">
                {cart.strategy.existingSmartphone
                  ? t("pricingCalc.summary.usePhone")
                  : t("pricingCalc.summary.dedicatedPos")}
              </span>
            </div>
            <button
              onClick={() => handleEditStep("device-strategy")}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Edit
            </button>
          </div>

          {/* Hardware */}
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <div>
              <span className="text-gray-700">
                {t("pricingCalc.summary.hardwareItems")}
              </span>
              <span className="ml-2 font-medium dark:text-gray-800">
                {hardwareItems.length} selected
              </span>
            </div>
            <button
              onClick={() => handleEditStep("hardware")}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Edit
            </button>
          </div>

          {/* Software Plan */}
          {!cart.eligibility.isFree && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <div>
                <span className="text-gray-700">Software Plan:</span>
                <span className="ml-2 font-medium dark:text-gray-800">
                  {cart.termMonths} months
                </span>
              </div>
              <button
                onClick={() => handleEditStep("plan")}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hardware Items */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("pricingCalc.summary.hardwareItems")}
        </h3>

        {hardwareItems.length > 0 ? (
          <div className="space-y-3">
            {hardwareItems.map(
              (item, index) =>
                item && (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 group relative hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start space-x-4">
                      {item.image && (
                        <div className="relative flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          {/* Hover Image Preview */}
                          <div className="absolute top-0 left-full ml-4 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                            <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-96">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-80 object-cover rounded-lg"
                              />
                              <div className="mt-4 text-center">
                                <p className="text-lg font-medium text-gray-900">
                                  {item.name}
                                </p>
                                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                                  {pickDescription(item)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-lg mb-1">
                              {item.name}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                              {pickDescription(item)}
                            </p>
                            <div className="flex items-center mt-2 space-x-2">
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                {item.availability}
                              </span>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                {item.category}
                              </span>
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="font-bold text-gray-900 text-lg">
                              {formatCurrency(
                                item.price.value,
                                item.price.currency
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatCurrency(
                                convert(item.price, cart.fx, "RWF"),
                                "RWF"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
            )}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            {t("pricingCalc.summary.noHardware")}
          </p>
        )}
      </div>

      {/* Software Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("pricingCalc.summary.software")}
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between items-center py-2">
            <div>
              <div className="font-medium text-gray-900">
                {cart.selections.software?.name}
              </div>
              <div className="text-sm text-gray-600">
                {cart.eligibility.isFree
                  ? t("pricingCalc.summary.rdbFree")
                  : `${cart.termMonths} ${t("pricingCalc.summary.monthPlan")}`}
              </div>
            </div>
            <div className="text-right">
              <div className="font-medium text-gray-900">
                {cart.eligibility.isFree
                  ? t("pricingCalc.summary.free")
                  : formatCurrency(cart.totals.software.value, "RWF")}
              </div>
              {!cart.eligibility.isFree && cart.totals.discountRate && (
                <div className="text-sm text-green-600">
                  {Math.round(cart.totals.discountRate * 100)}% discount applied
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("pricingCalc.summary.pricingSummary")}
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-blue-200">
            <span className="text-gray-700">
              {t("pricingCalc.summary.hardwareTotal")}
            </span>
            <span className="font-medium text-lg dark:text-gray-800">
              {formatCurrency(cart.totals.hardware.value, "RWF")}
            </span>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-blue-200">
            <span className="text-gray-700">
              {t("pricingCalc.summary.softwareTotal")}
            </span>
            <span className="font-medium text-lg dark:text-gray-800">
              {formatCurrency(cart.totals.software.value, "RWF")}
            </span>
          </div>

          {cart.totals.additionalUsers.value > 0 && (
            <div className="flex justify-between items-center py-2 border-b border-blue-200">
              <span className="text-gray-700">
                {t("pricingCalc.summary.additionalUsers")} ({cart.userCount - 2}
                )
              </span>
              <span className="font-medium text-lg">
                {formatCurrency(cart.totals.additionalUsers.value, "RWF")}
              </span>
            </div>
          )}

          {!cart.eligibility.isFree && cart.totals.discountRate && (
            <div className="flex justify-between items-center py-2 border-b border-blue-200">
              <span className="text-green-600">
                {t("pricingCalc.summary.softwareDiscount")}
              </span>
              <span className="font-medium text-green-600">
                -{Math.round(cart.totals.discountRate * 100)}%
              </span>
            </div>
          )}

          <div className="flex justify-between items-center py-4">
            <span className="text-xl font-bold text-gray-900">
              {t("pricingCalc.summary.grandTotal")}
            </span>
            <span className="text-2xl font-bold text-blue-600">
              {formatCurrency(cart.totals.grandTotal.value, "RWF")}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
        <button
          onClick={() => generatePDF(cart)}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span>{t("pricingCalc.summary.downloadPdf")}</span>
        </button>

        <button
          onClick={() => {
            const link = encodeCartToUrl(cart);
            if (
              navigator &&
              navigator.clipboard &&
              navigator.clipboard.writeText
            ) {
              navigator.clipboard.writeText(link).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }
          }}
          className={`px-8 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
            copied
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-gray-600 text-white hover:bg-gray-700"
          }`}
          title={
            copied
              ? t("pricingCalc.summary.copied") || "Copied"
              : t("pricingCalc.summary.copyLink") || "Copy Link"
          }
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <span>
            {copied
              ? t("pricingCalc.summary.copied")
              : t("pricingCalc.summary.copyLink")}
          </span>
        </button>

        <a
          href={generateWhatsAppShareLink(cart)}
          target="_blank"
          rel="noopener noreferrer"
          className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
          </svg>
          <span>{t("pricingCalc.summary.shareWhatsapp")}</span>
        </a>
      </div>

      {/* Additional Information */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.726-1.36 3.491 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-yellow-800">
              {t("pricingCalc.summary.nextSteps")}
            </h4>
            <div className="mt-2 text-sm text-yellow-700">
              <p>{t("pricingCalc.summary.nextStepsInfo")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryStep;
